import React, { PropsWithChildren, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { MimicUserProfile } from '../interfaces/MimicUserProfile';
import { MimicTarget } from '../types/MimicTarget';
import { getMimicContext } from '../utils/getMimicContext';

export type MimicProviderProps = PropsWithChildren<{
  onMimicChange?: (newMimicUserData: MimicTarget | undefined) => void;
}>;

export function MimicProvider({ children, onMimicChange }: MimicProviderProps) {
  const MimicContext = getMimicContext();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [showMimicModal, setShowMimicModal] = useState(false);

  // URL params are the source of truth for Apollo headers only
  const emailFromUrl = searchParams.get('mimicUser') ?? undefined;
  const companyIdFromUrl = searchParams.get('mimicCompany') ?? undefined;
  const isMimic = !!emailFromUrl && !!companyIdFromUrl;

  const SESSION_KEY = 'mimicUser';

  // Display info initialized from sessionStorage so it survives page reloads
  const [mimicUser, setMimicUserInfo] = useState<MimicUserProfile | undefined>(
    () => {
      const stored = sessionStorage.getItem(SESSION_KEY);
      return stored ? (JSON.parse(stored) as MimicUserProfile) : undefined;
    }
  );

  useEffect(() => {
    if (isMimic && !mimicUser) {
      navigate('/');
    }
  }, [isMimic, mimicUser, navigate]);

  useEffect(() => {
    if (isMimic && emailFromUrl && companyIdFromUrl) {
      onMimicChange?.({
        email: emailFromUrl,
        companyId: companyIdFromUrl,
      });
    } else {
      sessionStorage.removeItem(SESSION_KEY);
      onMimicChange?.(undefined);
      setMimicUserInfo(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailFromUrl, companyIdFromUrl]);

  const setMimicUser = (user: MimicUserProfile | undefined) => {
    if (user) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(user));
    } else {
      sessionStorage.removeItem(SESSION_KEY);
    }
    setMimicUserInfo(user);
    if (!user) {
      navigate(location.pathname);
    }
  };

  return (
    <MimicContext.Provider
      value={{
        mimicUser,
        isMimic,
        status: 'idle',
        showMimicModal,
        setShowMimicModal,
        setMimicUser,
      }}
    >
      {children}
    </MimicContext.Provider>
  );
}
